import { useTheme } from 'next-themes'
import { useEffect, useMemo, useRef, useState } from 'react'

import { loadGoogleLibrary } from '@/lib/google-maps-loader'

import { mountSupplierMap, parseCoord, resolveMapId } from './map'

type Props = {
  latitude: string | null
  longitude: string | null
}

const INITIAL_ZOOM = 13

export function LocationMiniMap({ latitude, longitude }: Props) {
  /** memoized so its identity only changes when the coords do, letting the
   * map effect depend on the object directly without re-running per render.
   */
  const center = useMemo(
    () => parseCoord(latitude, longitude),
    [latitude, longitude]
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>(
    'loading'
  )
  /* a locations list can hold a dozen of these and each is a full maps
   * instance, so only the ones scrolled to get built. */
  const [visible, setVisible] = useState(false)
  const { resolvedTheme } = useTheme()
  const mapId = resolveMapId()

  useEffect(() => {
    const frame = frameRef.current
    if (!frame || visible) return
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) setVisible(true)
      },
      { rootMargin: '200px' }
    )
    observer.observe(frame)
    return () => observer.disconnect()
  }, [visible])

  useEffect(() => {
    if (!center) {
      setStatus('unavailable')
      return
    }
    if (!visible) return
    let cancelled = false
    void Promise.all([
      loadGoogleLibrary('maps'),
      loadGoogleLibrary('marker')
    ]).then(([mapsLoaded, markerLoaded]) => {
      if (cancelled) return
      if (!mapsLoaded || !markerLoaded || !containerRef.current) {
        setStatus('unavailable')
        return
      }
      /** google maps has no destroy api and colorScheme is construction-only,
       * so wipe and rebuild on theme change to avoid stacking maps.
       */
      containerRef.current.innerHTML = ''
      mountSupplierMap(containerRef.current, center, {
        mapId,
        isDark: resolvedTheme === 'dark',
        zoom: INITIAL_ZOOM,
        pinScale: 0.8
      })
      setStatus('ready')
    })
    return () => {
      cancelled = true
    }
  }, [center, mapId, resolvedTheme, visible])

  if (!center) {
    return (
      <div className='grid aspect-[4/3] place-items-center rounded-md border border-dashed border-border bg-muted/40 text-[11px] text-muted-foreground'>
        Map unavailable
      </div>
    )
  }

  return (
    <div
      ref={frameRef}
      className='relative aspect-[4/3] overflow-hidden rounded-md border border-border'
    >
      <div ref={containerRef} className='size-full' />
      {status === 'loading' ? (
        <div className='absolute inset-0 grid place-items-center bg-muted/40 text-[11px] text-muted-foreground'>
          Loading map…
        </div>
      ) : null}
      {status === 'unavailable' ? (
        <div className='absolute inset-0 grid place-items-center bg-muted/40 text-[11px] text-muted-foreground'>
          Map unavailable.
        </div>
      ) : null}
    </div>
  )
}
